import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { MagnifyingGlass, BookOpenText, Newspaper, UsersThree, ChatCircleText, X } from "@phosphor-icons/react";

const EMPTY = { mangas: [], news: [], members: [], topics: [] };

export default function GlobalSearch({ onNavigate, autoFocus }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    function onClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(EMPTY); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q: q.trim() } });
        setResults(data);
        setOpen(true);
        setActive(0);
      } catch { setResults(EMPTY); }
      finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const flat = [
    ...results.mangas.map(m => ({ type: "manga", key: m.manga_id, label: m.title, sub: m.publisher, img: m.cover_url, to: `/manga/${m.slug || m.manga_id}` })),
    ...results.news.map(n => ({ type: "news", key: n.news_id, label: n.title, sub: "Notícia", img: n.image_url, to: `/noticias/${n.news_id}` })),
    ...results.members.map(u => ({ type: "member", key: u.user_id, label: u.name, sub: "Membro", img: u.picture, to: `/membros/${u.slug || u.user_id}` })),
    ...results.topics.map(t => ({ type: "topic", key: t.topic_id, label: t.title, sub: t.category || "Tópico", to: `/comunidade/${t.topic_id}` })),
  ];

  function go(item) {
    setOpen(false);
    setQ("");
    setResults(EMPTY);
    onNavigate?.();
    navigate(item.to);
  }

  function onKeyDown(e) {
    if (!open || flat.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  }

  const ICONS = { manga: BookOpenText, news: Newspaper, member: UsersThree, topic: ChatCircleText };

  return (
    <div className="relative w-full max-w-md" ref={boxRef} data-testid="global-search">
      <div className="flex items-center gap-2 h-10 px-3 rounded-lg bg-white/5 border border-white/10 focus-within:border-[#E63946]/50 transition-colors">
        <MagnifyingGlass size={16} weight="bold" className="text-zinc-400 flex-shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Pesquisar mangás, notícias, membros..."
          className="bg-transparent outline-none text-sm w-full placeholder:text-zinc-500"
          data-testid="global-search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults(EMPTY); }} className="text-zinc-500 hover:text-white flex-shrink-0" data-testid="global-search-clear">
            <X size={14} weight="bold" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-[#121217] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading && flat.length === 0 && (
            <div className="p-6 text-center text-zinc-500 font-mono-accent text-xs">A PESQUISAR...</div>
          )}
          {!loading && flat.length === 0 && (
            <div className="p-6 text-center text-zinc-500 font-mono-accent text-xs" data-testid="global-search-empty">SEM RESULTADOS PARA "{q}"</div>
          )}
          {flat.map((item, i) => {
            const Icon = ICONS[item.type];
            return (
              <button
                key={`${item.type}-${item.key}`}
                onClick={() => go(item)}
                onMouseEnter={() => setActive(i)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${active === i ? "bg-white/8" : "hover:bg-white/5"}`}
                data-testid={`search-result-${item.type}-${item.key}`}
              >
                {item.img ? (
                  <img src={resolveImageUrl(item.img)} alt="" className={`flex-shrink-0 object-cover ${item.type === "member" ? "w-9 h-9 rounded-full" : "w-8 h-10 rounded"}`} />
                ) : (
                  <div className="w-9 h-9 rounded bg-white/5 grid place-items-center flex-shrink-0 text-zinc-400">
                    <Icon size={16} weight="bold" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-display font-semibold truncate">{item.label}</div>
                  <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
                    <Icon size={11} weight="bold" /> {item.sub}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
